import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: '-modification_date' };

const createState = () => ({
    syncs: [],
    syncs_url: '/api/connectors/pos/syncs/',
    syncs_filters: { ...DEFAULT_FILTERS },
    syncs_count: 0,
    syncs_total_count: null,
    syncs_total_loading: false,
    syncs_loaded: false,
    syncs_loading: false,
    syncs_loading_errors: null,
    syncs_cancel_source: null,
});

const mutations = {
    updateSyncs(state, syncs) {
        state.syncs = syncs;
    },
    updateSyncsFilters(state, syncs_filters) {
        state.syncs_filters = syncs_filters;
    },
    updateSyncsLoading(state, syncs_loading) {
        state.syncs_loading = syncs_loading;
    },
    updateSyncsLoaded(state, syncs_loaded) {
        state.syncs_loaded = syncs_loaded;
    },
    updateSyncsLoadingErrors(state, syncs_loading_errors) {
        state.syncs_loading_errors = syncs_loading_errors;
    },
    updateSyncsCount(state, syncs_count) {
        state.syncs_count = syncs_count;
    },
    updateSyncsTotalCount(state, syncs_total_count) {
        state.syncs_total_count = syncs_total_count;
    },
    updateSyncsTotalLoading(state, syncs_total_loading) {
        state.syncs_total_loading = syncs_total_loading;
    },
    updateSyncsCancelSource(state, source) {
        state.syncs_cancel_source = source;
    },
};

const actions = {
    async fetchSyncsTotal({ commit, state }) {
        if (store.getters['session/current_user_permissions'].indexOf('connectors.view_pos_sync') === -1) {
            return;
        }

        commit('updateSyncsTotalLoading', true);
        commit('updateSyncsTotalCount', null);
        try {
            const establishmentId = state.syncs_filters.establishment;
            if (!establishmentId) {
                commit('updateSyncsTotalCount', 0);
                commit('updateSyncs', []);
                commit('updateSyncsCount', 0);
                return 0;
            }
            const response = await axios.get(state.syncs_url, {
                params: {
                    establishment: establishmentId,
                    limit: 0,
                },
            });
            const count = response.data.count ?? 0;
            commit('updateSyncsTotalCount', count);
            if (count === 0) {
                commit('updateSyncs', []);
                commit('updateSyncsCount', 0);
                commit('updateSyncsLoaded', true);
            }
            return count;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateSyncsTotalLoading', false);
        }
    },

    async fetchSyncs({ commit, state }) {
        if (store.getters['session/current_user_permissions'].indexOf('connectors.view_pos_sync') === -1) {
            return;
        }

        commit('updateSyncsLoading', true);
        commit('updateSyncsLoadingErrors', null);

        if (state.syncs_cancel_source) {
            state.syncs_cancel_source.cancel('canceled');
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateSyncsCancelSource', cancelSource);
        try {
            const response = await axios.get(state.syncs_url, {
                params: state.syncs_filters,
                cancelToken: cancelSource.token,
            });
            commit('updateSyncs', response.data.results);
            commit('updateSyncsCount', response.data.count);
            commit('updateSyncsCancelSource', null);
            commit('updateSyncsLoaded', true);
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateSyncsLoadingErrors', error.details);
            commit('updateSyncsCancelSource', null);
            throw error;
        } finally {
            commit('updateSyncsLoading', false);
        }
    },

    async init({ commit }, params) {
        await commit('updateSyncs', []);
        await commit('updateSyncsLoaded', false);
        await commit('updateSyncsFilters', {
            ...DEFAULT_FILTERS,
            ...(params.filters || {}),
        });
    },
};

export default () => ({
    namespaced: true,
    state: createState(),
    mutations,
    actions,
});
